import React, { useState } from 'react';
import { setProjectWorkWeek } from '../../../api';
import useConfirm from '../../../hooks/useConfirm';
import ConfirmDialog from '../../ConfirmDialog';

/* The schedule's work week (#52): Mon–Fri (the default) or every day. Applied at once rather
   than through the Save bar, because changing it recalculates every task that hasn't started
   (durations stay the same), as changing the calendar does in Microsoft Project. */
const WorkWeekPicker = ({ projectId, value, onChanged }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const week = String(value) === '7' ? '7' : '5';
  const choose = async (next) => {
    if (busy || next === week) return;
    const ok = await confirm({
      title: 'Change the work week?',
      message: next === '7'
        ? "Every day becomes a working day. Every task that hasn't started is recalculated; durations stay the same."
        : "Weekends stop being working days. Every task that hasn't started is recalculated; durations stay the same.",
      confirmLabel: next === '7' ? 'Use 7 days' : 'Use Mon–Fri',
    });
    if (!ok) return;
    setBusy(true);
    setNote('');
    try {
      const r = await setProjectWorkWeek(projectId, next);
      const n = (r && r.schedule_moved) || 0;
      setNote(n ? `${n} task${n === 1 ? '' : 's'} recalculated` : 'No tasks needed to move');
      if (onChanged) onChanged(next);
    } catch (err) {
      setNote(`Couldn't change it: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  const option = (key, label) => (
    <button
      type="button" onClick={() => choose(key)} disabled={busy} aria-pressed={week === key}
      style={{
        height: 28, padding: '0 12px', border: 'none', borderRadius: 7, font: 'inherit', fontSize: 13,
        cursor: busy ? 'default' : 'pointer', fontWeight: week === key ? 600 : 500,
        background: week === key ? 'var(--bg-surface)' : 'transparent',
        color: week === key ? 'var(--text-1)' : 'var(--text-3)',
        boxShadow: week === key ? 'var(--shadow-xs)' : 'none',
      }}
    >
      {label}
    </button>
  );
  return (
    <div>
      <div style={{ display: 'inline-flex', gap: 2, padding: 2, borderRadius: 9, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
        {option('5', 'Mon–Fri')}
        {option('7', '7 days')}
      </div>
      {note && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{note}</div>}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default WorkWeekPicker;
